// Pieces of the full-screen action row (FullScreenActionRow.js): the crossfade
// cell, the quiet text button, and the fixed PRIMARY SLOT — the one box that
// hosts the idle button AND the save/send beat so the primary never changes
// size or place between steps and beats.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { foundation } from '../../../ui';
import { BEAT_EASE } from '../../../ui/actionBeatMotion';
import { overlayActionDisabled } from '../../../ui/overlayStyles';
import { SPIN_ANIMATION, ensureSpinStyle } from '../../../utils/ui/spinAnimation';
import { wizardPrimaryButtonStyle } from '../routeOverride/transferWizardStyles';
import ActionBeatFill from '../actionBeat/ActionBeatFill';
import useLeavingSwap, { BAR_CROSSFADE_MS, stripIdentity } from './useLeavingSwap';

const { spacePx, color, fontSize, fontWeight, fontFamily } = foundation;

/** The primary's fixed box: 44px tall on every device, every step, every beat. */
export const FULLSCREEN_PRIMARY_SLOT_HEIGHT = 44;
/** Row-height floor on phone/tablet (phone = the stacked total's measured height). */
export const FULLSCREEN_BAR_ROW_MIN = { phone: 57, tablet: 48 };
/** Desktop split tween (fr columns + gap), matching ActionBeatFill's own trick. */
export const FULLSCREEN_ACTION_SPLIT_MS = 320;

const STYLE_ID = 'cvx-action-row-sizer';
function ensureSizerStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  // The widest idle label sizes the slot; the label text is drawn by CSS so
  // the DOM carries each label once (the live one).
  s.textContent = '.cvx-action-sizer::after { content: attr(data-label); }';
  document.head.appendChild(s);
}

/** Opt-in colour fade of the idle primary (`primary.fadeColor`). */
const COLOR_FADE = 'background-color 200ms ease, border-color 200ms ease, color 200ms ease';

const layer = { gridArea: '1 / 1', minWidth: 0 };
const fade = (name) => `${name} ${BAR_CROSSFADE_MS}ms ease both`;

/**
 * One grid cell whose contents crossfade (old fades out, new fades in) when
 * `swapKey` changes. The dying copy has no test ids / element ids.
 */
export function CrossfadeCell({ swapKey, reduced, children, style, testId }) {
  const { leaving, animateIn } = useLeavingSwap(swapKey, children, reduced);
  return (
    <div style={{ display: 'grid', ...style }} data-testid={testId}>
      <div key={swapKey} style={animateIn ? { ...layer, animation: fade('cvx-bar-in') } : layer}>{children}</div>
      {leaving ? (
        <div
          key={`out-${leaving.key}`}
          style={{ ...layer, animation: fade('cvx-bar-out'), pointerEvents: 'none' }}
          ref={stripIdentity}
          inert
          aria-hidden
          data-leaving
        >
          {leaving.node}
        </div>
      ) : null}
    </div>
  );
}

const quiet = {
  background: 'none',
  border: 'none',
  padding: `${spacePx[2]} ${spacePx[1]}`,
  minHeight: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
  color: color.brand.text,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.sans,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  whiteSpace: 'nowrap',
};

/**
 * The quiet text action ("Send later"): never a second filled button.
 * `block` fills its column (desktop split); `hidden` fades it out and makes it inert (mid-beat).
 */
export function FullScreenQuietButton({ label, onClick, testId, block = false, hidden = false }) {
  return (
    <button
      type="button"
      style={{
        ...quiet,
        ...(block ? { width: '100%' } : null),
        opacity: hidden ? 0 : 1,
        transition: 'opacity 200ms ease',
      }}
      onClick={onClick}
      disabled={hidden}
      tabIndex={hidden ? -1 : undefined}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

// The idle button's box == the beat's box: pinned height, horizontal padding only.
const boxPadding = `0 ${spacePx[4]}`;
const idleButton = {
  ...wizardPrimaryButtonStyle,
  width: '100%',
  height: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
  minHeight: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
  padding: boxPadding,
  boxSizing: 'border-box',
  flex: 'none',
  minWidth: 0,
};
const beatBox = {
  height: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
  minHeight: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
  padding: boxPadding,
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
};
const sizerBox = {
  gridArea: '1 / 1',
  display: 'inline-grid',
  visibility: 'hidden',
  height: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
  padding: boxPadding,
  border: '1px solid transparent',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.sans,
  pointerEvents: 'none',
  justifySelf: 'start', // never stretches when the slot grows: it measures the NATURAL width
};
const sizerLabel = { gridArea: '1 / 1' };
const liveLayer = { position: 'absolute', inset: 0 };
// Opt-in (`primary.danger`): the idle primary of a destructive flow wears the danger colours (same box).
const DANGER_PAINT = {
  background: color.semantic.dangerFill,
  backgroundColor: color.semantic.dangerFill,
  border: `1px solid ${color.semantic.dangerFill}`,
};

/**
 * The primary slot. Touch: sized by the WIDEST idle label (a grid cell that
 * stacks every label invisibly — right in EN and ES and for any future flow),
 * never by the live label, a working label or the beat. Desktop: fills its
 * grid column. Height is 44 everywhere.
 *
 * `primary.pending` (KAN-772): a small spinner INSIDE the same button — same
 * box, same colour, NOT dimmed, still tappable (the host ignores the repeat
 * tap). For "I'll do it the moment I'm ready" actions such as Edit while the
 * job is still loading; `busy` stays the dimmed, disabled "working" look.
 *
 * @param {{label: string, labels?: string[], onClick: () => void, testId?: string, busy?: boolean, busyLabel?: string, disabled?: boolean, muted?: boolean, pending?: boolean, fadeColor?: boolean, danger?: boolean}} props.primary
 *   `danger` (opt-in): the idle button is red and the beat plays the delete tone with the danger button (default false).
 * @param {{phase: 'working'|'success', workingLabel: string, successLabel: string, reduced?: boolean, tone?: string}|null} props.beat
 */
export function FullScreenPrimarySlot({ primary, beat, touch, reduced, fill = false }) {
  ensureSpinStyle();
  useLayoutEffect(() => { ensureSizerStyle(); }, []);
  const busy = Boolean(primary.busy);
  const pending = Boolean(primary.pending) && !busy;
  const disabled = busy || Boolean(primary.disabled);
  // `muted` (opt-in): the quiet disabled LOOK while the button stays tappable (a tap that redirects, e.g. to the first blank field).
  const mutedLook = disabled || Boolean(primary.muted);
  const sizerLabels = [...new Set([primary.label, ...(primary.labels || [])].filter(Boolean))];
  // Touch: the slot rests at its natural (widest idle label) width, measured
  // from the sizer into px so flex-basis can tween; during a beat it GROWS to
  // the full row (flex-grow 0 -> 1) while the row's left cell collapses, on
  // the same 320ms clock as the desktop split. No transforms.
  const sizerRef = useRef(null);
  const [natural, setNatural] = useState(0);
  useLayoutEffect(() => {
    const el = sizerRef.current;
    if (!touch || !el) return undefined;
    const measure = () => setNatural(Math.round(el.offsetWidth) || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [touch, sizerLabels.join('\u0000')]); // eslint-disable-line react-hooks/exhaustive-deps
  const tween = reduced ? 'none' : `flex-grow ${FULLSCREEN_ACTION_SPLIT_MS}ms ${BEAT_EASE.out}`;
  const slotStyle = touch
    ? {
      position: 'relative', display: 'grid', minWidth: 0, height: FULLSCREEN_PRIMARY_SLOT_HEIGHT,
      flexGrow: beat || fill ? 1 : 0, flexShrink: 0, flexBasis: natural > 0 ? `${natural}px` : 'auto',
      transition: tween,
    }
    : { position: 'relative', width: '100%', minWidth: 0, height: FULLSCREEN_PRIMARY_SLOT_HEIGHT };

  // The beat -> idle hand-off (green "Created" -> blue "Send"): the idle
  // button mounts underneath and the beat layer STAYS MOUNTED on top, fading
  // out on a timer, so it never snaps (reduced motion = instant). While a beat
  // is live the idle button is unmounted (one primary test id at a time).
  const lastBeat = useRef(beat);
  if (beat) lastBeat.current = beat;
  const [hadBeat, setHadBeat] = useState(Boolean(beat));
  const [lingerBeat, setLingerBeat] = useState(null);
  if (Boolean(beat) !== hadBeat) {
    setHadBeat(Boolean(beat));
    setLingerBeat(beat || reduced ? null : lastBeat.current);
  }
  useEffect(() => {
    if (!lingerBeat) return undefined;
    const timer = setTimeout(() => setLingerBeat(null), BAR_CROSSFADE_MS + 80);
    return () => clearTimeout(timer);
  }, [lingerBeat]);
  const beatShown = beat || lingerBeat;
  const lingering = !beat && Boolean(lingerBeat);

  const idle = (
    <button
      type="button"
      style={{
        ...(mutedLook ? { ...idleButton, ...overlayActionDisabled } : idleButton),
        ...(primary.danger && !disabled ? DANGER_PAINT : null),
        // Opt-in (KAN-778, Guardar): grey <-> blue is a calm 200ms colour fade;
        // only colours move, the box never changes size. Reduced motion = instant.
        ...(primary.fadeColor ? { transition: reduced ? 'none' : COLOR_FADE } : null),
      }}
      onClick={primary.onClick}
      disabled={disabled}
      aria-busy={pending || undefined}
      data-pending={pending || undefined}
      data-testid={primary.testId}
    >
      {busy || pending ? (
        <>
          <Loader2 size={14} strokeWidth={2.4} aria-hidden style={{ animation: SPIN_ANIMATION, flexShrink: 0 }} />
          {busy ? (primary.busyLabel || primary.label) : primary.label}
        </>
      ) : (
        <CrossfadeCell swapKey={primary.label} reduced={reduced} style={{ justifyItems: 'center', minWidth: 0 }}>
          {primary.label}
        </CrossfadeCell>
      )}
    </button>
  );

  return (
    <div style={slotStyle} data-testid="fullscreen-action-primary-slot" data-slot-kind={touch ? 'touch' : 'column'}>
      {touch ? (
        <span aria-hidden style={sizerBox} ref={sizerRef} data-testid="fullscreen-action-sizer">
          {sizerLabels.map((l) => <span key={l} className="cvx-action-sizer" data-label={l} style={sizerLabel} />)}
        </span>
      ) : null}
      {beat ? null : <div style={liveLayer}>{idle}</div>}
      {beatShown ? (
        <div
          key="beat"
          style={lingering ? { ...liveLayer, animation: `cvx-bar-out ${BAR_CROSSFADE_MS}ms ease both`, pointerEvents: 'none' } : liveLayer}
          inert={lingering || undefined}
          aria-hidden={lingering || undefined}
          data-lingering={lingering || undefined}
        >
          <ActionBeatFill
            phase={beatShown.phase === 'success' ? 'success' : 'working'}
            tone={primary.danger ? 'delete' : (beatShown.tone || 'save')}
            buttonTone={primary.danger ? 'danger' : 'primary'}
            confirmLabel={primary.label}
            onConfirm={() => {}}
            onCancel={() => {}}
            workingLabel={beatShown.workingLabel}
            successLabel={beatShown.successLabel}
            reduced={beatShown.reduced ?? reduced}
            rowStyle={{ width: '100%', height: '100%' }}
            confirmStyle={beatBox}
            // While the slot shrinks back after a beat (a failed send) the
            // lingering label is centred and clipped at both ends, never "..."
            // for the ~5 frames the box is narrower than the text (KAN-768).
            ellipsize={lingering ? 'clip' : true}
            sequentialLabels={Boolean(beatShown.sequentialLabels)}
          />
        </div>
      ) : null}
    </div>
  );
}
